import * as THREE from 'three'

const FACE_HEIGHT = 192
const MIN_FACE_WIDTH = 96
const MAX_FACE_WIDTH = 560
const textureCache = new Map()
const damageTextureCache = new Map()

const BLOCK_ART = {
    wood: {
        side: 0x7b4a23,
        cap: 0x9b5d2c,
        roughness: 0.78,
        metalness: 0.02,
        draw: drawWoodFace
    },
    stone: {
        side: 0x5e6874,
        cap: 0x7f8893,
        roughness: 0.88,
        metalness: 0.02,
        draw: drawStoneFace
    },
    glass: {
        side: 0x3f9fb4,
        cap: 0x78d7e7,
        roughness: 0.28,
        metalness: 0.04,
        transparent: true,
        opacity: 0.72,
        draw: drawGlassFace
    }
}

export function createBlockMaterials(materialKey, width, height) {
    const art = BLOCK_ART[materialKey] || BLOCK_ART.wood
    const texture = getBlockTexture(materialKey, width, height)
    const sideSettings = {
        roughness: art.roughness,
        metalness: art.metalness,
        transparent: art.transparent || false,
        opacity: art.opacity ?? 1
    }
    const sideX = new THREE.MeshStandardMaterial({
        ...sideSettings,
        color: art.side
    })
    const sideY = new THREE.MeshStandardMaterial({
        ...sideSettings,
        color: art.cap
    })
    const face = new THREE.MeshStandardMaterial({
        ...sideSettings,
        color: 0xffffff,
        map: texture
    })

    return [
        sideX,
        sideX.clone(),
        sideY,
        sideY.clone(),
        face,
        face.clone()
    ]
}

export function createDamageOverlayMaterial(materialKey, width, height, state = 'heavy') {
    return new THREE.MeshBasicMaterial({
        color: 0xffffff,
        map: getDamageOverlayTexture(materialKey, width, height, state),
        transparent: true,
        opacity: getDamageOverlayOpacity(state),
        depthWrite: false,
        side: THREE.DoubleSide
    })
}

function getBlockTexture(materialKey, width, height) {
    const key = `${materialKey}:${roundSize(width)}x${roundSize(height)}`

    if (textureCache.has(key)) {
        return textureCache.get(key)
    }

    const art = BLOCK_ART[materialKey] || BLOCK_ART.wood
    const aspect = clamp(width / height, 0.32, 4.8)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(clamp(FACE_HEIGHT * aspect, MIN_FACE_WIDTH, MAX_FACE_WIDTH))
    canvas.height = FACE_HEIGHT

    const ctx = canvas.getContext('2d')
    art.draw(ctx, canvas.width, canvas.height, makeRandom(hashString(key)))

    const texture = new THREE.CanvasTexture(canvas)
    texture.userData.cachedBlockTexture = true
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearMipmapLinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.generateMipmaps = true
    texture.needsUpdate = true
    textureCache.set(key, texture)
    return texture
}

function getDamageOverlayTexture(materialKey, width, height, state) {
    const key = `damage:${materialKey}:${state}:${roundSize(width)}x${roundSize(height)}`

    if (damageTextureCache.has(key)) {
        return damageTextureCache.get(key)
    }

    const aspect = clamp(width / height, 0.32, 4.8)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(clamp(FACE_HEIGHT * aspect, MIN_FACE_WIDTH, MAX_FACE_WIDTH))
    canvas.height = FACE_HEIGHT

    const ctx = canvas.getContext('2d')
    drawDamageOverlay(ctx, canvas.width, canvas.height, materialKey, state, makeRandom(hashString(key)))

    const texture = new THREE.CanvasTexture(canvas)
    texture.userData.cachedBlockTexture = true
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearMipmapLinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.generateMipmaps = true
    texture.needsUpdate = true
    damageTextureCache.set(key, texture)
    return texture
}

function getDamageOverlayOpacity(state) {
    if (state === 'destroyed') {
        return 0.94
    }

    return state === 'light' ? 0.54 : 0.78
}

function drawDamageOverlay(ctx, width, height, materialKey, state, random) {
    const severity = state === 'destroyed' ? 1 : state === 'heavy' ? 0.72 : 0.38
    const crackCount = Math.max(
        state === 'light' ? 2 : 4,
        Math.round((width + height) / (state === 'light' ? 150 : 105))
    )

    ctx.clearRect(0, 0, width, height)
    ctx.save()

    if (materialKey === 'glass') {
        drawGlassDamage(ctx, width, height, severity, crackCount, random)
    } else if (materialKey === 'stone') {
        drawStoneDamage(ctx, width, height, severity, crackCount, random)
    } else {
        drawWoodDamage(ctx, width, height, severity, crackCount, random)
    }

    if (state === 'destroyed') {
        scatterChips(ctx, width, height, random, 'rgba(18, 22, 27, 0.34)', Math.round(18 + severity * 18))
        ctx.globalAlpha = 0.2
        ctx.fillStyle = '#121820'
        ctx.fillRect(0, 0, width, height)
    }

    ctx.restore()
}

function drawStoneDamage(ctx, width, height, severity, crackCount, random) {
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    for (let index = 0; index < crackCount; index += 1) {
        const x = width * (0.12 + random() * 0.76)
        const y = height * (0.12 + random() * 0.72)
        const length = Math.min(width, height) * (0.2 + severity * 0.35 + random() * 0.18)
        const angle = -1.15 + random() * 2.3

        drawJaggedCrack(ctx, x, y, length, angle, {
            branches: severity > 0.55 ? 2 + Math.round(random() * 2) : 1,
            color: `rgba(36, 42, 51, ${0.42 + severity * 0.32})`,
            highlight: `rgba(235, 240, 246, ${0.18 + severity * 0.1})`,
            width: Math.max(1.3, Math.min(width, height) * (0.012 + severity * 0.01)),
            random
        })
    }
}

function drawWoodDamage(ctx, width, height, severity, crackCount, random) {
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    const longAxisIsWide = width >= height
    for (let index = 0; index < crackCount; index += 1) {
        const x = width * (0.12 + random() * 0.76)
        const y = height * (0.14 + random() * 0.68)
        const length = (longAxisIsWide ? width : height) * (0.18 + severity * 0.18 + random() * 0.1)
        const angle = longAxisIsWide
            ? (random() - 0.5) * 0.42
            : Math.PI / 2 + (random() - 0.5) * 0.42

        drawJaggedCrack(ctx, x, y, length, angle, {
            branches: severity > 0.6 ? 2 : 1,
            color: `rgba(48, 24, 10, ${0.44 + severity * 0.36})`,
            highlight: `rgba(255, 210, 132, ${0.14 + severity * 0.08})`,
            width: Math.max(1.4, Math.min(width, height) * (0.014 + severity * 0.012)),
            random
        })
    }
}

function drawGlassDamage(ctx, width, height, severity, crackCount, random) {
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    for (let index = 0; index < crackCount; index += 1) {
        const x = width * (0.16 + random() * 0.68)
        const y = height * (0.16 + random() * 0.68)
        const spokes = 3 + Math.round(random() * (severity > 0.6 ? 4 : 2))
        const baseLength = Math.min(width, height) * (0.13 + severity * 0.26)

        for (let spoke = 0; spoke < spokes; spoke += 1) {
            drawJaggedCrack(ctx, x, y, baseLength * (0.62 + random() * 0.62), (Math.PI * 2 * spoke) / spokes + random() * 0.48, {
                branches: severity > 0.7 ? 1 : 0,
                color: `rgba(231, 255, 255, ${0.42 + severity * 0.32})`,
                highlight: `rgba(24, 103, 120, ${0.14 + severity * 0.18})`,
                width: Math.max(1.1, Math.min(width, height) * (0.01 + severity * 0.006)),
                random
            })
        }
    }
}

function drawJaggedCrack(ctx, x, y, length, angle, options) {
    const segments = 3 + Math.round(options.random() * 3)
    const points = [{ x, y }]

    for (let index = 1; index <= segments; index += 1) {
        const progress = index / segments
        const wobble = (options.random() - 0.5) * length * 0.16
        points.push({
            x: x + Math.cos(angle) * length * progress + Math.cos(angle + Math.PI / 2) * wobble,
            y: y + Math.sin(angle) * length * progress + Math.sin(angle + Math.PI / 2) * wobble
        })
    }

    ctx.save()
    ctx.strokeStyle = options.highlight
    ctx.lineWidth = options.width * 1.6
    strokePolyline(ctx, points)
    ctx.strokeStyle = options.color
    ctx.lineWidth = options.width
    strokePolyline(ctx, points)

    for (let branch = 0; branch < options.branches; branch += 1) {
        const origin = points[1 + Math.floor(options.random() * Math.max(1, points.length - 2))]
        const branchAngle = angle + (options.random() > 0.5 ? 1 : -1) * (0.58 + options.random() * 0.66)
        const branchLength = length * (0.22 + options.random() * 0.22)
        ctx.beginPath()
        ctx.moveTo(origin.x, origin.y)
        ctx.lineTo(origin.x + Math.cos(branchAngle) * branchLength, origin.y + Math.sin(branchAngle) * branchLength)
        ctx.stroke()
    }

    ctx.restore()
}

function strokePolyline(ctx, points) {
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)

    for (let index = 1; index < points.length; index += 1) {
        ctx.lineTo(points[index].x, points[index].y)
    }

    ctx.stroke()
}

function drawWoodFace(ctx, width, height, random) {
    fillVerticalGradient(ctx, width, height, '#df9740', '#9b5d2b')
    addInsetFrame(ctx, width, height, '#f3c16d', '#6f3f1e', 0.42)

    const longAxisIsWide = width >= height
    const seamCount = longAxisIsWide
        ? Math.max(1, Math.round(width / height))
        : Math.max(1, Math.round(height / width))

    ctx.save()
    ctx.lineCap = 'round'

    for (let index = 1; index < seamCount; index += 1) {
        const progress = index / seamCount
        ctx.strokeStyle = 'rgba(92, 48, 20, 0.38)'
        ctx.lineWidth = Math.max(2, Math.min(width, height) * 0.02)
        ctx.beginPath()

        if (longAxisIsWide) {
            const x = width * progress + (random() - 0.5) * width * 0.035
            ctx.moveTo(x, height * 0.12)
            ctx.lineTo(x + (random() - 0.5) * width * 0.025, height * 0.88)
        } else {
            const y = height * progress + (random() - 0.5) * height * 0.035
            ctx.moveTo(width * 0.12, y)
            ctx.lineTo(width * 0.88, y + (random() - 0.5) * height * 0.025)
        }

        ctx.stroke()
    }

    const grainLines = Math.max(8, Math.round((width + height) / 44))
    for (let index = 0; index < grainLines; index += 1) {
        const y = height * (0.16 + random() * 0.68)
        const wobble = height * (0.035 + random() * 0.04)
        ctx.strokeStyle = random() > 0.35 ? 'rgba(116, 58, 22, 0.32)' : 'rgba(255, 221, 144, 0.22)'
        ctx.lineWidth = 1.2 + random() * 2.4
        ctx.beginPath()
        ctx.moveTo(width * -0.06, y)
        ctx.bezierCurveTo(width * 0.22, y - wobble, width * 0.38, y + wobble, width * 0.62, y)
        ctx.bezierCurveTo(width * 0.78, y - wobble * 0.8, width * 0.94, y + wobble, width * 1.06, y)
        ctx.stroke()
    }

    const knotCount = Math.max(1, Math.min(3, Math.round(width / height)))
    for (let index = 0; index < knotCount; index += 1) {
        const x = width * (0.22 + random() * 0.56)
        const y = height * (0.25 + random() * 0.5)
        const rx = Math.min(width, height) * (0.08 + random() * 0.05)
        const ry = rx * (0.5 + random() * 0.24)
        const angle = (random() - 0.5) * 0.7

        ctx.fillStyle = 'rgba(112, 52, 18, 0.52)'
        ctx.beginPath()
        ctx.ellipse(x, y, rx, ry, angle, 0, Math.PI * 2)
        ctx.fill()

        ctx.strokeStyle = 'rgba(255, 218, 141, 0.24)'
        ctx.lineWidth = Math.max(1.2, rx * 0.14)
        ctx.beginPath()
        ctx.ellipse(x, y, rx * 0.62, ry * 0.58, angle, 0, Math.PI * 2)
        ctx.stroke()
    }

    drawBolt(ctx, width * 0.1, height * 0.5, Math.min(width, height) * 0.052)
    drawBolt(ctx, width * 0.9, height * 0.5, Math.min(width, height) * 0.052)
    ctx.restore()
}

function drawStoneFace(ctx, width, height, random) {
    fillVerticalGradient(ctx, width, height, '#b6bec7', '#7c8792')
    addInsetFrame(ctx, width, height, '#d9dee4', '#4f5864', 0.36)

    const aspect = width / height
    const rows = aspect > 1.5 ? 2 : 3
    const cols = Math.max(2, Math.min(6, Math.round(aspect * 2.5)))
    const gap = Math.max(4, Math.min(width, height) * 0.035)

    ctx.save()

    for (let row = 0; row < rows; row += 1) {
        const rowTop = gap + row * ((height - gap * 2) / rows)
        const rowHeight = (height - gap * 2) / rows
        const offset = row % 2 === 0 ? 0 : width / cols * 0.42

        for (let col = 0; col < cols + 1; col += 1) {
            const cellWidth = width / cols
            const x = col * cellWidth - offset + gap
            const y = rowTop + gap * 0.45
            const w = cellWidth - gap * 1.2
            const h = rowHeight - gap

            if (x > width - gap || x + w < gap) {
                continue
            }

            const shade = Math.round(128 + random() * 34)
            ctx.fillStyle = `rgb(${shade}, ${shade + 8}, ${shade + 17})`
            roundedRect(ctx, Math.max(gap, x), y, Math.min(w, width - x - gap), h, gap * 0.6)
            ctx.fill()

            ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)'
            ctx.lineWidth = Math.max(1.2, gap * 0.18)
            ctx.stroke()
        }
    }

    const crackCount = Math.max(2, Math.round((width + height) / 170))
    for (let index = 0; index < crackCount; index += 1) {
        const startX = width * (0.18 + random() * 0.64)
        const startY = height * (0.18 + random() * 0.58)
        const length = Math.min(width, height) * (0.18 + random() * 0.25)
        ctx.strokeStyle = 'rgba(55, 62, 72, 0.38)'
        ctx.lineWidth = Math.max(1.4, Math.min(width, height) * 0.014)
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()
        ctx.moveTo(startX, startY)
        ctx.lineTo(startX + length * (random() - 0.35), startY + length * 0.45)
        ctx.lineTo(startX + length * (0.15 + random() * 0.35), startY + length * (0.7 + random() * 0.35))
        ctx.stroke()
    }

    scatterChips(ctx, width, height, random, 'rgba(58, 66, 76, 0.24)', 13)
    ctx.restore()
}

function drawGlassFace(ctx, width, height, random) {
    fillVerticalGradient(ctx, width, height, '#b8f5ff', '#4dbdd1')
    addInsetFrame(ctx, width, height, '#ecffff', '#197d93', 0.32)

    ctx.save()
    ctx.globalCompositeOperation = 'screen'
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.72)'
    ctx.lineWidth = Math.max(6, Math.min(width, height) * 0.09)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(width * 0.08, height * 0.78)
    ctx.lineTo(width * 0.38, height * 0.2)
    ctx.stroke()

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.36)'
    ctx.lineWidth = Math.max(3, Math.min(width, height) * 0.045)
    ctx.beginPath()
    ctx.moveTo(width * 0.58, height * 0.88)
    ctx.lineTo(width * 0.86, height * 0.34)
    ctx.stroke()

    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = 'rgba(31, 123, 142, 0.38)'
    ctx.lineWidth = Math.max(1.4, Math.min(width, height) * 0.014)
    ctx.lineCap = 'round'

    for (let index = 0; index < 3; index += 1) {
        const x = width * (0.22 + random() * 0.56)
        const y = height * (0.18 + random() * 0.56)
        const r = Math.min(width, height) * (0.035 + random() * 0.04)
        ctx.beginPath()
        ctx.arc(x, y, r, 0, Math.PI * 2)
        ctx.stroke()
    }

    const crackX = width * (0.54 + random() * 0.18)
    const crackY = height * (0.18 + random() * 0.22)
    const crackLength = Math.min(width, height) * 0.32
    ctx.strokeStyle = 'rgba(235, 255, 255, 0.58)'
    ctx.lineWidth = Math.max(1.2, Math.min(width, height) * 0.012)
    ctx.beginPath()
    ctx.moveTo(crackX, crackY)
    ctx.lineTo(crackX - crackLength * 0.25, crackY + crackLength * 0.32)
    ctx.moveTo(crackX, crackY)
    ctx.lineTo(crackX + crackLength * 0.28, crackY + crackLength * 0.22)
    ctx.moveTo(crackX, crackY)
    ctx.lineTo(crackX + crackLength * 0.06, crackY + crackLength * 0.58)
    ctx.stroke()
    ctx.restore()
}

function fillVerticalGradient(ctx, width, height, top, bottom) {
    const gradient = ctx.createLinearGradient(0, 0, 0, height)
    gradient.addColorStop(0, top)
    gradient.addColorStop(1, bottom)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
}

function addInsetFrame(ctx, width, height, highlight, shadow, opacity) {
    const inset = Math.max(5, Math.min(width, height) * 0.045)

    ctx.save()
    ctx.globalAlpha = opacity
    ctx.lineWidth = inset
    ctx.strokeStyle = highlight
    ctx.beginPath()
    ctx.moveTo(inset * 0.5, height - inset * 0.5)
    ctx.lineTo(inset * 0.5, inset * 0.5)
    ctx.lineTo(width - inset * 0.5, inset * 0.5)
    ctx.stroke()

    ctx.strokeStyle = shadow
    ctx.beginPath()
    ctx.moveTo(width - inset * 0.5, inset * 0.5)
    ctx.lineTo(width - inset * 0.5, height - inset * 0.5)
    ctx.lineTo(inset * 0.5, height - inset * 0.5)
    ctx.stroke()
    ctx.restore()
}

function drawBolt(ctx, x, y, radius) {
    ctx.save()
    ctx.fillStyle = 'rgba(70, 42, 22, 0.42)'
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = 'rgba(255, 214, 130, 0.36)'
    ctx.beginPath()
    ctx.arc(x - radius * 0.22, y - radius * 0.25, radius * 0.32, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
}

function scatterChips(ctx, width, height, random, color, count) {
    ctx.save()
    ctx.fillStyle = color

    for (let index = 0; index < count; index += 1) {
        const size = Math.min(width, height) * (0.012 + random() * 0.024)
        const x = width * (0.08 + random() * 0.84)
        const y = height * (0.12 + random() * 0.76)
        ctx.beginPath()
        ctx.moveTo(x, y - size)
        ctx.lineTo(x + size * 0.9, y)
        ctx.lineTo(x + size * 0.22, y + size)
        ctx.lineTo(x - size * 0.8, y + size * 0.35)
        ctx.closePath()
        ctx.fill()
    }

    ctx.restore()
}

function roundedRect(ctx, x, y, width, height, radius) {
    const r = Math.min(radius, width / 2, height / 2)
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + width - r, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + r)
    ctx.lineTo(x + width, y + height - r)
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
    ctx.lineTo(x + r, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
}

function roundSize(value) {
    return Math.round(value * 100) / 100
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value))
}

function hashString(value) {
    let hash = 2166136261

    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }

    return hash >>> 0
}

function makeRandom(seed) {
    return () => {
        seed += 0x6d2b79f5
        let value = seed
        value = Math.imul(value ^ (value >>> 15), value | 1)
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296
    }
}
